import classNames from 'classnames';
import { Link } from 'react-router';

import {
  courseCurriculumActivityClicked,
  // courseCurriculumActivityFavorited,
  courseCurriculumFavoriteActivityClicked,
} from 'my-core/gtm-events';
import { getActivityTypeConfig, getOCActivityMetadataLabel } from 'my-core/online-course-activity-utils';
import { canViewOnlineCourseActivity } from 'my-core/online-course-utils';

import LatexString from 'my-elements/LatexString';

import FreeContentLabel from './FreeContentLabel';
import MetadataText from './MetadataText';

import Avatar from '@mui/material/Avatar';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { faLockAlt } from '@fortawesome/pro-light-svg-icons';
import { faCheckCircle, faDotCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const rootCss = ({ palette, spacing }) => ({
  padding: spacing(1, 2, 1, 7),
  display: 'flex',
  justifyContent: 'flex-start',
  textAlign: 'left',
  width: '100%',

  '&.root_topLevel': {
    borderBottom: `1px solid ${palette.divider}`,
    padding: spacing(2, 2, 2, 3),
    backgroundColor: palette.background.paper,
  },

  '&.root_selected, &.root_selected:hover': { backgroundColor: palette.primary.background },
  '&.root_selected .Act-icon': { backgroundColor: palette.primary.main },
  '&.root_selected .Act-text': { color: palette.text.primary },

  '.Act-icon': {
    width: 16,
    height: 16,
    minWidth: 0,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing(1),

    fontSize: 10,
    color: palette.common.white,
    backgroundColor: palette.text.secondary,
    borderRadius: '50%',
  },
  '.Act-text': {
    flex: 1,
    color: palette.text.primary,
    lineHeight: 1.3,
  },

  '.Act-statusIcon': {
    borderRadius: '50%',
  },
  '.Act-statusIcon_complete': {
    color: palette.green.main,
    backgroundColor: palette.green.contrastText,
  },
  '.Act-statusIcon_inProgress': {
    color: palette.yellow.main,
    backgroundColor: palette.common.white,
  },
  '.Act-meta + svg': { marginLeft: spacing(1) },

  '.Act-action': {
    right: 12,

    '& button': {
      width: 24,
      height: 24,
      padding: 0,
    },
    '& svg': { fontSize: 12 },
  },
  '.Act-actionIcon': { color: palette.text.secondary },
  '.Act-actionIcon_active': { color: palette.primary.main },
});

export default function Activity({
  active,
  activity,
  canPreviewAll,
  canViewAll,
  className,
  component,
  displayMeta,
  eventTrackingProperties,
  isFavorites,
  onlineCourseUser,
  onLinkClick,
  // onSetFavorite,
  path,
  showViewableIcon,
  title,
  tokenPurchases,
  topLevel,
  topLevelClassName,
}) {
  const progress = onlineCourseUser?.progress?.completed?.find(p => p.activity_id === activity.id);
  // const favorite = !!progress?.starred;

  const canView = canViewOnlineCourseActivity(component, { canPreviewAll, canViewAll, tokenPurchases });
  const canPreview = canPreviewAll || canView;

  const btnProps = {};
  if (canPreview) {
    btnProps.component = Link;
    btnProps.onClick = () => {
      onLinkClick?.();
      if (isFavorites) courseCurriculumFavoriteActivityClicked(eventTrackingProperties);
      else courseCurriculumActivityClicked(eventTrackingProperties);
    };
    btnProps.to = path;
  } else {
    btnProps.disabled = true;
  }

  const metaData = getOCActivityMetadataLabel(activity);

  const typeConfig = getActivityTypeConfig(activity);

  return (
    <ButtonBase
      className={classNames(className, topLevel && topLevelClassName, {
        root_topLevel: topLevel,
        root_selected: active,
      })}
      css={rootCss}
      {...btnProps}
    >
      <Tooltip title={typeConfig.label}>
        <Avatar className="Act-icon">
          <FontAwesomeIcon fixedWidth icon={typeConfig.icon} size="xs" />
        </Avatar>
      </Tooltip>
      <Typography className="Act-text" variant={topLevel ? 'body2' : 'caption'}>
        <LatexString>{title}</LatexString>
      </Typography>
      {showViewableIcon && activity.can_preview && <FreeContentLabel />}
      {progress && (
        <FontAwesomeIcon
          className={classNames('Act-statusIcon', {
            'Act-statusIcon_complete': progress.is_completed,
            'Act-statusIcon_inProgress': !progress.is_completed,
          })}
          icon={progress.is_completed ? faCheckCircle : faDotCircle}
          size="xs"
        />
      )}
      {(canPreview || displayMeta) && metaData && <MetadataText className="Act-meta">{metaData}</MetadataText>}
      {renderActivityAction()}
    </ButtonBase>
  );

  function renderActivityAction() {
    if (!canPreview || (!canView && activity.activity_metadata?.is_private)) {
      return <FontAwesomeIcon className="Act-actionIcon" fixedWidth icon={faLockAlt} size="sm" />;
    }

    // if (onlineCourseUser && onSetFavorite) {
    //   return (
    //     <ListItemSecondaryAction className="Act-action">
    //       <IconButton
    //         onClick={() => {
    //           if (!favorite) courseCurriculumActivityFavorited(activity, eventTrackingProperties);
    //           onSetFavorite(id, { starred: !favorite }, { immediate: true });
    //         }}
    //       >
    //         <FontAwesomeIcon
    //           className={classNames('Act-actionIcon', { 'Act-actionIcon_active': favorite })}
    //           fixedWidth
    //           icon={favorite ? faBookmarkSolid : faBookmark}
    //           size="sm"
    //         />
    //       </IconButton>
    //     </ListItemSecondaryAction>
    //   );
    // }
  }
}
